/**
 * Email Collector JavaScript
 * Handles form interactions, validation, and user experience enhancements
 */
document.addEventListener('DOMContentLoaded', function() {
  const emailCollectors = document.querySelectorAll('.email-collector');
  
  emailCollectors.forEach(collector => {
    initEmailCollector(collector);
  });
});

function initEmailCollector(collector) {
  const form = collector.querySelector('.email-collector__form');
  const input = collector.querySelector('.email-collector__input');
  const button = collector.querySelector('.email-collector__button');
  const notifications = collector.querySelectorAll('.email-collector__notification');
  
  if (!form || !input || !button) return;
  
  // Add form submission handling
  form.addEventListener('submit', handleFormSubmit);
  
  // Add input validation and UX enhancements
  input.addEventListener('input', handleInputChange);
  input.addEventListener('blur', handleInputBlur);
  input.addEventListener('focus', handleInputFocus);
  
  // Add button loading state
  button.addEventListener('click', handleButtonClick);
  
  // Add keyboard navigation
  input.addEventListener('keydown', handleKeyDown);
  
  // Initialize animations
  initAnimations(collector);
  
  // Add intersection observer for performance
  addIntersectionObserver(collector);
}

function handleFormSubmit(e) {
  const form = e.target;
  const input = form.querySelector('.email-collector__input');
  const button = form.querySelector('.email-collector__button');
  
  // Validate email before submission
  if (!isValidEmail(input.value)) {
    e.preventDefault();
    showNotification(form, 'Please enter a valid email address', 'error');
    input.focus();
    return;
  }
  
  // Add loading state
  setButtonLoading(button, true);
  
  // Clear any existing notifications
  clearNotifications(form);
}

function handleInputChange(e) {
  const input = e.target;
  const form = input.closest('.email-collector__form');
  
  // Clear notifications on input change
  clearNotifications(form);
  
  // Add real-time validation feedback
  if (input.value.length > 0) {
    if (isValidEmail(input.value)) {
      input.classList.add('valid');
      input.classList.remove('invalid');
    } else {
      input.classList.add('invalid');
      input.classList.remove('valid');
    }
  } else {
    input.classList.remove('valid', 'invalid');
  }
}

function handleInputBlur(e) {
  const input = e.target;
  const form = input.closest('.email-collector__form');
  
  // Validate on blur
  if (input.value.length > 0 && !isValidEmail(input.value)) {
    showNotification(form, 'Please enter a valid email address', 'error');
  }
}

function handleInputFocus(e) {
  const input = e.target;
  const form = input.closest('.email-collector__form');
  
  // Clear notifications on focus
  clearNotifications(form);
}

function handleButtonClick(e) {
  const button = e.target;
  const form = button.closest('.email-collector__form');
  const input = form.querySelector('.email-collector__input');
  
  // Focus input if empty
  if (!input.value.trim()) {
    e.preventDefault();
    input.focus();
    showNotification(form, 'Please enter your email address', 'error');
  }
}

function handleKeyDown(e) {
  const input = e.target;
  const form = input.closest('.email-collector__form');
  const button = form.querySelector('.email-collector__button');
  
  // Submit on Enter key
  if (e.key === 'Enter') {
    e.preventDefault();
    button.click();
  }
  
  // Clear notifications on Escape
  if (e.key === 'Escape') {
    clearNotifications(form);
    input.blur();
  }
}

function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

function showNotification(form, message, type = 'error') {
  // Remove existing notifications
  clearNotifications(form);
  
  // Create notification element
  const notification = document.createElement('div');
  notification.className = `email-collector__notification ${type}`;
  notification.textContent = message;
  
  // Add icon based on type
  const icon = type === 'error' ? '⚠️' : '✅';
  notification.innerHTML = `${icon} ${message}`;
  
  // Insert notification
  const formContainer = form.querySelector('.email-collector__form-container');
  formContainer.insertBefore(notification, formContainer.firstChild);
  
  // Auto-remove after 5 seconds
  setTimeout(() => {
    if (notification.parentNode) {
      notification.remove();
    }
  }, 5000);
  
  // Add animation
  notification.style.opacity = '0';
  notification.style.transform = 'translateY(-10px)';
  
  requestAnimationFrame(() => {
    notification.style.transition = 'all 0.3s ease';
    notification.style.opacity = '1';
    notification.style.transform = 'translateY(0)';
  });
}

function clearNotifications(form) {
  const notifications = form.querySelectorAll('.email-collector__notification');
  notifications.forEach(notification => {
    notification.style.transition = 'all 0.3s ease';
    notification.style.opacity = '0';
    notification.style.transform = 'translateY(-10px)';
    
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove();
      }
    }, 300);
  });
}

function setButtonLoading(button, loading) {
  if (loading) {
    button.disabled = true;
    button.classList.add('loading');
    button.innerHTML = '<span class="loading-spinner"></span> Subscribing...';
  } else {
    button.disabled = false;
    button.classList.remove('loading');
    // Restore original text from data attribute
    button.innerHTML = button.getAttribute('data-original-text') || 'Join the movement';
  }
}

function initAnimations(collector) {
  const elements = collector.querySelectorAll('.email-collector__inner > *');
  
  // Add staggered animation delays
  elements.forEach((element, index) => {
    element.style.animationDelay = `${index * 0.1}s`;
  });
  
  // Add hover effects
  const button = collector.querySelector('.email-collector__button');
  if (button) {
    button.addEventListener('mouseenter', () => {
      button.style.transform = 'translateY(-2px)';
    });
    
    button.addEventListener('mouseleave', () => {
      button.style.transform = 'translateY(0)';
    });
  }
  
  // Add background image hover effect
  const backgroundImage = collector.querySelector('.email-collector__background-image');
  if (backgroundImage) {
    backgroundImage.addEventListener('mouseenter', () => {
      backgroundImage.style.transform = 'scale(1.02)';
    });
    
    backgroundImage.addEventListener('mouseleave', () => {
      backgroundImage.style.transform = 'scale(1)';
    });
  }
}

function addIntersectionObserver(collector) {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('in-view');
        
        // Trigger animations
        const elements = entry.target.querySelectorAll('.email-collector__inner > *');
        elements.forEach((element, index) => {
          setTimeout(() => {
            element.classList.add('animate-in');
          }, index * 100);
        });
      }
    });
  }, {
    threshold: 0.1
  });
  
  observer.observe(collector);
}

// Handle form success/error states from Shopify
document.addEventListener('shopify:section:load', function() {
  const emailCollectors = document.querySelectorAll('.email-collector');
  
  emailCollectors.forEach(collector => {
    initEmailCollector(collector);
  });
});

// Add CSS for loading spinner
const style = document.createElement('style');
style.textContent = `
  .email-collector__button.loading {
    position: relative;
    pointer-events: none;
  }
  
  .loading-spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid transparent;
    border-top: 2px solid currentColor;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-right: 8px;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .email-collector__input.valid {
    border-color: #22c55e;
  }
  
  .email-collector__input.invalid {
    border-color: #dc2626;
  }
  
  .email-collector__inner > *.animate-in {
    animation: fadeInUp 0.6s ease forwards;
  }
`;
document.head.appendChild(style);

// Accessibility improvements
document.addEventListener('keydown', function(e) {
  const activeCollector = document.activeElement.closest('.email-collector');
  
  if (activeCollector) {
    const form = activeCollector.querySelector('.email-collector__form');
    const input = form.querySelector('.email-collector__input');
    const button = form.querySelector('.email-collector__button');
    
    switch(e.key) {
      case 'Tab':
        // Ensure proper tab order
        if (e.shiftKey && document.activeElement === button) {
          e.preventDefault();
          input.focus();
        } else if (!e.shiftKey && document.activeElement === input) {
          e.preventDefault();
          button.focus();
        }
        break;
    }
  }
});

// Reduced motion support
if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  const style = document.createElement('style');
  style.textContent = `
    .email-collector__inner > * {
      animation: none !important;
    }
    
    .email-collector__button:hover {
      transform: none !important;
    }
    
    .loading-spinner {
      animation: none !important;
    }
  `;
  document.head.appendChild(style);
}

